import { Bell, LogOut, Menu, Search, UserRound } from 'lucide-react'
import { Link, useNavigate } from 'react-router'

import { session, useCan, useSessionStore } from '@/auth'
import { Button, CountBadge, DropdownMenu, IconButton, Pill, Tooltip } from '@/design'
import { useUnreadCount } from '@/features/notifications/queries'
import { LocationSwitcher } from './location-switcher'

export function Topbar({
  onOpenNav,
  onOpenPalette,
}: {
  readonly onOpenNav: () => void
  readonly onOpenPalette: () => void
}) {
  const user = useSessionStore((state) => state.user)
  const canNotifications = useCan('notifications:read')
  const unread = useUnreadCount()
  const navigate = useNavigate()
  const count = unread.data?.unread ?? 0

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-line bg-panel/90 px-3 backdrop-blur sm:gap-3 sm:px-5">
      <IconButton label="Open navigation" variant="ghost" className="md:hidden" onClick={onOpenNav}>
        <Menu />
      </IconButton>
      <LocationSwitcher />
      <div className="flex-1" />
      <Tooltip content="Go to a page or a customer (⌘K / Ctrl+K)">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<Search className="size-4" />}
          className="hidden sm:inline-flex"
          onClick={onOpenPalette}
        >
          Search
          <kbd className="ml-1 rounded-sm border border-line px-1 font-mono text-[10px] text-muted-2">
            ⌘K
          </kbd>
        </Button>
      </Tooltip>
      {canNotifications && (
        <Link
          to="/notifications"
          className="relative inline-flex size-9 items-center justify-center rounded-sm border border-transparent text-ink-2 hover:bg-ink/5"
          aria-label={count > 0 ? `Notifications, ${String(count)} unread` : 'Notifications'}
        >
          <Bell aria-hidden className="size-4" />
          <CountBadge count={count} className="absolute -top-1 -right-1" />
        </Link>
      )}
      <DropdownMenu
        label="Account"
        trigger={
          <button
            type="button"
            className="flex h-9 items-center gap-2 rounded-sm border border-transparent px-1.5 text-left hover:bg-ink/5"
            aria-label="Account menu"
          >
            <span
              aria-hidden
              className="flex size-7 items-center justify-center rounded-full bg-brand text-[12px] font-bold text-brand-fg"
            >
              {(user?.email[0] ?? '?').toUpperCase()}
            </span>
            <span className="hidden min-w-0 md:block">
              <span className="block max-w-[180px] truncate text-[12.5px] font-semibold text-ink">
                {user?.email}
              </span>
              <Pill tone="neutral" className="mt-0.5 px-1.5 py-0 text-[9.5px]">
                {user?.role.replace(/_/g, ' ')}
              </Pill>
            </span>
          </button>
        }
        items={[
          {
            id: 'profile',
            label: 'Profile & password',
            icon: <UserRound />,
            onSelect: () => void navigate('/settings/profile'),
          },
          {
            id: 'logout',
            label: 'Sign out',
            icon: <LogOut />,
            separatorBefore: true,
            onSelect: () => void session.logout(),
          },
        ]}
      />
    </header>
  )
}
